"use client";

import { AvatarLevel } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarFit } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";
import cardStyles from "./AvatarDetailCard/AvatarDetailCard.module.css";
import AvatarScoresGrid from "./AvatarScoresGrid/AvatarScoresGrid";
import ScoreCardHeader from "./ScoreCardHeader/ScoreCardHeader";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
  params: { lang: string };
};

const listPlaceholder = [
  { cells: [{ width: "85%" }] },
  { cells: [{ width: "70%" }] },
  { cells: [{ width: "80%" }] },
];

export default function AvatarDetailFit(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, params, isAnalyzing = false } = props;
  const { lang } = params;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const queryClient = useQueryClient();
  const router = useRouter();

  const { data: fitData, isLoading: isFetching } = useFetchAvatarFit({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = { ...avatar, ...(fitData || {}) } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const renderBadge = (level: string | null, placeholderText: string, defaultStyle: string) => {
    if (isPlaceholder) {
      return (
        <span className={UStyles.classNames(styles.levelBadge, defaultStyle)}>
          {placeholderText}
        </span>
      );
    }
    if (!level) return null;

    let style = styles.levelMedium;
    let text = level;
    if (level === AvatarLevel.HIGH) {
      style = styles.levelHigh;
      text = T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_HIGH);
    } else if (level === AvatarLevel.MEDIUM) {
      style = styles.levelMedium;
      text = T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_MEDIUM);
    } else if (level === AvatarLevel.LOW) {
      style = styles.levelLow;
      text = T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_LOW);
    }

    return <span className={UStyles.classNames(styles.levelBadge, style)}>{text}</span>;
  };

  const handleSaveList = async (fieldKey: AvatarField, newItems: string[]) => {
    try {
      await updateAvatarField(avatar.offerId, fieldKey, newItems);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.FIT,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const handleSaveText = async (fieldKey: AvatarField, val: string) => {
    try {
      await updateAvatarField(avatar.offerId, fieldKey, val);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.FIT,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.scoreCardWrapper}>
        <ScoreCardHeader
          params={params}
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_TITLE)}
          showInfo={
            (fullAvatar.scoreFit == null || fullAvatar.scoreFit === 0) &&
            (fullAvatar.scoreConfidence == null || fullAvatar.scoreConfidence === 0) &&
            (fullAvatar.scorePain == null || fullAvatar.scorePain === 0)
          }
        />
        <AvatarScoresGrid
          avatar={fullAvatar}
          isPlaceholder={isPlaceholder}
          showFitSummary={true}
          params={params}
        />
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY)}
          badge={renderBadge(
            fullAvatar.urgencyLevel,
            T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_HIGH),
            styles.levelHigh,
          )}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[{ cells: [{ width: "60px" }] }, { cells: [{ width: "85%" }] }]}
        >
          {isPlaceholder ? null : (
            <EditableText
              value={fullAvatar.urgencyDescription || ""}
              onSave={(val) => handleSaveText(AvatarField.URGENCY_DESCRIPTION, val)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_SENTENCE}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_PLACEHOLDER)}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_PURCHASE_POTENTIAL)}
          badge={renderBadge(
            fullAvatar.purchasePotential,
            T(TKey.CLIENT_AVATAR_DETAIL_FIT_URGENCY_MEDIUM),
            styles.levelMedium,
          )}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[{ cells: [{ width: "60px" }] }, { cells: [{ width: "85%" }] }]}
        >
          {isPlaceholder ? null : (
            <EditableText
              value={fullAvatar.purchasePotentialDescription || ""}
              onSave={(val) => handleSaveText(AvatarField.PURCHASE_POTENTIAL_DESCRIPTION, val)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_SENTENCE}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_FIT_PURCHASE_POTENTIAL_PLACEHOLDER)}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_IDEAL_IF)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.idealClientCriteria || []}
              onSave={(items) => handleSaveList(AvatarField.IDEAL_CLIENT_CRITERIA, items)}
              listClassName={UStyles.classNames(styles.dotList, styles.dotListGreen)}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
              hideBullet={true}
              lang={lang}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_NON_IDEAL_IF)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.nonIdealClientCriteria || []}
              onSave={(items) => handleSaveList(AvatarField.NON_IDEAL_CLIENT_CRITERIA, items)}
              listClassName={UStyles.classNames(styles.dotList, styles.dotListRed)}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
              hideBullet={true}
              lang={lang}
            />
          )}
        </AvatarDetailCard>
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_TRIGGERS)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.purchaseTriggers || []}
              onSave={(items) => handleSaveList(AvatarField.PURCHASE_TRIGGERS, items)}
              bulletColor="var(--color-analysis-objection)"
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_WHERE_TO_FIND)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={listPlaceholder}
        >
          {isPlaceholder ? null : (
            <EditableList
              items={fullAvatar.whereToFind || []}
              onSave={(items) => handleSaveList(AvatarField.WHERE_TO_FIND, items)}
              itemMaxLength={Limits.MAX_AVATAR_LIST_ITEM}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      <div className={styles.twoColGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_VOICE_OF_TRUST)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[{ cells: [{ width: "100%" }] }, { cells: [{ width: "75%" }] }]}
        >
          {isPlaceholder ? null : (
            <EditableText
              value={fullAvatar.voiceOfTrust || ""}
              onSave={(val) => handleSaveText(AvatarField.VOICE_OF_TRUST, val)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_SENTENCE}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_FIT_VOICE_OF_TRUST_PLACEHOLDER)}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_FIT_APPROACH_ANGLE)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[{ cells: [{ width: "100%" }] }, { cells: [{ width: "80%" }] }]}
        >
          {isPlaceholder ? null : (
            <EditableText
              value={fullAvatar.approachAngle || ""}
              onSave={(val) => handleSaveText(AvatarField.APPROACH_ANGLE, val)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_SENTENCE}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_FIT_APPROACH_ANGLE_PLACEHOLDER)}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>
      </div>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
